import React, { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '../components/ui/table'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { adminApi } from '../services/adminApi'
import type { User, Invite } from '../types/admin'
import { toast } from 'sonner'

type StatusFilter = 'all' | 'waitlisted' | 'active'
type Tab = 'users' | 'invites'

export default function WaitlistPage() {
    const [activeTab, setActiveTab] = useState<Tab>('users')

    // Users state
    const [users, setUsers] = useState<User[]>([])
    const [usersLoading, setUsersLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('waitlisted')
    const [search, setSearch] = useState('')
    const [usersPagination, setUsersPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0,
    })
    const [counts, setCounts] = useState({ all: 0, waitlisted: 0, active: 0 })

    // Invites state
    const [invites, setInvites] = useState<Invite[]>([])
    const [invitesLoading, setInvitesLoading] = useState(true)
    const [invitesPagination, setInvitesPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0,
    })

    // Invite form
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteName, setInviteName] = useState('')
    const [sendingInvite, setSendingInvite] = useState(false)

    // Action in-flight
    const [actionId, setActionId] = useState<string | null>(null)

    const fetchUsers = useCallback(async () => {
        try {
            setUsersLoading(true)
            const data = await adminApi.getWaitlistUsers({
                page: usersPagination.page,
                limit: usersPagination.limit,
                search,
                status: statusFilter === 'all' ? undefined : statusFilter,
            })
            setUsers(data.users)
            setUsersPagination(data.pagination)
            setCounts(data.counts)
        } catch (error) {
            console.error('Failed to fetch users:', error)
        } finally {
            setUsersLoading(false)
        }
    }, [usersPagination.page, usersPagination.limit, search, statusFilter])

    const fetchInvites = useCallback(async () => {
        try {
            setInvitesLoading(true)
            const data = await adminApi.getInvites(invitesPagination.page, invitesPagination.limit)
            setInvites(data.invites)
            setInvitesPagination(data.pagination)
        } catch (error) {
            console.error('Failed to fetch invites:', error)
        } finally {
            setInvitesLoading(false)
        }
    }, [invitesPagination.page, invitesPagination.limit])

    useEffect(() => {
        fetchUsers()
    }, [fetchUsers])

    useEffect(() => {
        fetchInvites()
    }, [fetchInvites])

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Invite link copied to clipboard')
        }).catch(() => {
            // Fallback: show it in a prompt
            window.prompt('Copy this invite link:', text)
        })
    }

    const handleApprove = async (userId: string) => {
        try {
            setActionId(userId)
            const result = await adminApi.approveWaitlistedUser(userId)
            if ((result as any).emailError) {
                toast.warning((result as any).message, { duration: 6000 })
                if ((result as any).inviteLink) {
                    copyToClipboard((result as any).inviteLink)
                }
            } else {
                toast.success('User approved and invite sent')
            }
            fetchUsers()
            fetchInvites()
        } catch (error) {
            console.error('Failed to approve user:', error)
            toast.error('Failed to approve user')
        } finally {
            setActionId(null)
        }
    }

    const handleRevoke = async (userId: string) => {
        if (!confirm('Revoke this user\'s access? They will be moved back to the waitlist.')) return
        try {
            setActionId(userId)
            await adminApi.revokeUserAccess(userId)
            toast.success('User access revoked')
            fetchUsers()
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Failed to revoke access'
            toast.error(msg)
        } finally {
            setActionId(null)
        }
    }

    const handleSendInvite = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!inviteEmail.trim()) return

        try {
            setSendingInvite(true)
            const result = await adminApi.createInvite({
                email: inviteEmail.trim(),
                name: inviteName.trim() || undefined,
            })
            if ((result as any).emailError) {
                toast.warning((result as any).message, { duration: 6000 })
                if ((result as any).inviteLink) {
                    copyToClipboard((result as any).inviteLink)
                }
            } else {
                toast.success('Invite sent successfully')
            }
            setInviteEmail('')
            setInviteName('')
            fetchUsers()
            fetchInvites()
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Failed to send invite'
            toast.error(msg)
        } finally {
            setSendingInvite(false)
        }
    }


    const handleDeleteInvite = async (id: string) => {
        if (!confirm('Delete this invite?')) return
        try {
            await adminApi.deleteInvite(id)
            toast.success('Invite deleted')
            fetchInvites()
        } catch (error) {
            console.error('Failed to delete invite:', error)
            toast.error('Failed to delete invite')
        }
    }

    const handleStatusFilterChange = (filter: StatusFilter) => {
        setStatusFilter(filter)
        setUsersPagination((p) => ({ ...p, page: 1 }))
    }

    const handleUsersPageChange = (page: number) => {
        setUsersPagination((p) => ({ ...p, page }))
    }

    const handleInvitesPageChange = (page: number) => {
        setInvitesPagination((p) => ({ ...p, page }))
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-gray-900">Access Management</h1>
            </div>

            {/* Send Invite Form */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-left">Send Invite</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSendInvite} className="flex items-end gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                Email *
                            </label>
                            <Input
                                type="email"
                                placeholder="user@example.com"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                Name (optional)
                            </label>
                            <Input
                                placeholder="John Doe"
                                value={inviteName}
                                onChange={(e) => setInviteName(e.target.value)}
                            />
                        </div>
                        <Button type="submit" disabled={sendingInvite}>
                            {sendingInvite ? 'Sending...' : 'Send Invite'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Top-level tabs: Users / Invites */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                    onClick={() => setActiveTab('users')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'users'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Users ({counts.all})
                </button>
                <button
                    onClick={() => setActiveTab('invites')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'invites'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Invites ({invitesPagination.total})
                </button>
            </div>

            {/* ================ USERS TAB ================ */}
            {activeTab === 'users' && (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between w-full gap-4">
                            <div className="flex items-center gap-2">
                                {/* Status filter pills */}
                                {(
                                    [
                                        { key: 'all', label: 'All', count: counts.all },
                                        {
                                            key: 'waitlisted',
                                            label: 'Waitlisted',
                                            count: counts.waitlisted,
                                        },
                                        {
                                            key: 'active',
                                            label: 'Active',
                                            count: counts.active,
                                        },
                                    ] as const
                                ).map(({ key, label, count }) => (
                                    <button
                                        key={key}
                                        onClick={() => handleStatusFilterChange(key)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                                            statusFilter === key
                                                ? 'bg-blue-50 border-blue-300 text-blue-700'
                                                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                        }`}
                                    >
                                        {label} ({count})
                                    </button>
                                ))}
                            </div>
                            <Input
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value)
                                    setUsersPagination((p) => ({ ...p, page: 1 }))
                                }}
                                className="max-w-xs"
                            />
                        </div>
                    </CardHeader>
                    <CardContent>
                        {usersLoading && users.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">Loading...</div>
                        ) : users.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No users found</div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>User</TableHead>
                                        <TableHead>Organization</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Signed Up</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((user: any) => (
                                        <TableRow key={user.id}>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    {user.avatar ? (
                                                        <img
                                                            src={user.avatar}
                                                            alt={user.name || user.email}
                                                            className="w-8 h-8 rounded-full mr-3"
                                                        />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                                                            <span className="text-gray-500 text-xs font-medium">
                                                                {(user.name || user.email)
                                                                    .charAt(0)
                                                                    .toUpperCase()}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {user.name || 'No name'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600">
                                                    {user.organization?.name || 'Unknown'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        user.status === 'active'
                                                            ? 'success'
                                                            : 'warning'
                                                    }
                                                >
                                                    {user.status}
                                                </Badge>
                                                {user.invites?.[0] && (
                                                    <Badge
                                                        variant="info"
                                                        className="ml-1"
                                                    >
                                                        invited
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600">
                                                    {new Date(
                                                        user.createdAt
                                                    ).toLocaleDateString()}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {user.status === 'waitlisted' ? (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleApprove(user.id)}
                                                        disabled={actionId === user.id}
                                                    >
                                                        {actionId === user.id
                                                            ? 'Approving...'
                                                            : 'Approve'}
                                                    </Button>
                                                ) : user.status === 'active' ? (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleRevoke(user.id)}
                                                        disabled={actionId === user.id}
                                                        className="text-red-600 hover:text-red-700"
                                                    >
                                                        {actionId === user.id
                                                            ? 'Revoking...'
                                                            : 'Revoke Access'}
                                                    </Button>
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {usersPagination.pages > 1 && (
                            <div className="flex items-center justify-between mt-6">
                                <div className="text-sm text-gray-700">
                                    Showing{' '}
                                    {(usersPagination.page - 1) * usersPagination.limit + 1} to{' '}
                                    {Math.min(
                                        usersPagination.page * usersPagination.limit,
                                        usersPagination.total
                                    )}{' '}
                                    of {usersPagination.total} results
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleUsersPageChange(usersPagination.page - 1)
                                        }
                                        disabled={usersPagination.page <= 1}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-sm text-gray-700">
                                        Page {usersPagination.page} of {usersPagination.pages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleUsersPageChange(usersPagination.page + 1)
                                        }
                                        disabled={
                                            usersPagination.page >= usersPagination.pages
                                        }
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* ================ INVITES TAB ================ */}
            {activeTab === 'invites' && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-left">Sent Invites</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {invitesLoading && invites.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">Loading...</div>
                        ) : invites.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No invites yet</div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Sent</TableHead>
                                        <TableHead>Used</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {invites.map((invite) => (
                                        <TableRow key={invite.id}>
                                            <TableCell>
                                                <span className="text-gray-900">
                                                    {invite.email}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600">
                                                    {invite.name || '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        invite.status === 'accepted'
                                                            ? 'success'
                                                            : invite.status === 'sent'
                                                              ? 'info'
                                                              : 'default'
                                                    }
                                                >
                                                    {invite.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600">
                                                    {new Date(
                                                        invite.createdAt
                                                    ).toLocaleDateString()}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600">
                                                    {invite.usedAt
                                                        ? new Date(
                                                              invite.usedAt
                                                          ).toLocaleDateString()
                                                        : '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {invite.status !== 'accepted' && invite.inviteLink && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => copyToClipboard(invite.inviteLink!)}
                                                        >
                                                            Copy Link
                                                        </Button>
                                                    )}
                                                    {invite.status !== 'accepted' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleDeleteInvite(invite.id)
                                                            }
                                                            className="text-red-600 hover:text-red-700"
                                                        >
                                                            Delete
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {invitesPagination.pages > 1 && (
                            <div className="flex items-center justify-between mt-6">
                                <div className="text-sm text-gray-700">
                                    Showing{' '}
                                    {(invitesPagination.page - 1) * invitesPagination.limit + 1}{' '}
                                    to{' '}
                                    {Math.min(
                                        invitesPagination.page * invitesPagination.limit,
                                        invitesPagination.total
                                    )}{' '}
                                    of {invitesPagination.total} results
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleInvitesPageChange(invitesPagination.page - 1)
                                        }
                                        disabled={invitesPagination.page <= 1}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-sm text-gray-700">
                                        Page {invitesPagination.page} of {invitesPagination.pages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleInvitesPageChange(invitesPagination.page + 1)
                                        }
                                        disabled={
                                            invitesPagination.page >= invitesPagination.pages
                                        }
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
